import React, { useState } from 'react';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    serviceType: '',
    message: '',
    _honey: ''
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [errorMessage, setErrorMessage] = useState('');
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const validate = (data) => {
    const errs = {};
    if (!data.name.trim()) errs.name = 'Name is required.';
    if (!data.email.trim()) {
      errs.email = 'Email is required.';
    } else if (!EMAIL_REGEX.test(data.email)) {
      errs.email = 'Please enter a valid email address.';
    }
    if (!data.message.trim()) {
      errs.message = 'Message is required.';
    } else if (data.message.trim().length < 10) {
      errs.message = 'Message must be at least 10 characters.';
    }
    return errs;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updated = { ...formData, [name]: value };
    setFormData(updated);

    if (hasSubmitted && errors[name]) {
      const newErrors = validate(updated);
      setErrors((prev) => {
        const next = { ...prev };
        if (newErrors[name]) {
          next[name] = newErrors[name];
        } else {
          delete next[name];
        }
        return next;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errs = validate(formData);
    setErrors(errs);
    setHasSubmitted(true);

    if (Object.keys(errs).length > 0) return;

    // Honeypot: silently discard
    if (formData._honey) {
      setStatus('success');
      return;
    }

    setStatus('submitting');

    try {
      const res = await fetch(process.env.REACT_APP_CONTACT_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim(),
          company: formData.company.trim(),
          serviceType: formData.serviceType,
          message: formData.message.trim()
        })
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setStatus('success');
      } else {
        setErrorMessage(data.error || 'Something went wrong. Please try again.');
        setStatus('error');
      }
    } catch {
      setErrorMessage('Unable to send your message. Please check your connection and try again.');
      setStatus('error');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', phone: '', company: '', serviceType: '', message: '', _honey: '' });
    setErrors({});
    setStatus('idle');
    setErrorMessage('');
    setHasSubmitted(false);
  };

  if (status === 'success') {
    return (
      <div className="contact-text-container-blur">
        <div className="border-2 border-green-600 rounded-lg p-8 text-center">
          <svg className="mx-auto mb-4" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#046c4e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <path d="M9 12l2 2 4-4" />
          </svg>
          <h3 className="text-2xl font-bold text-green-700 mb-2">Thank You!</h3>
          <p className="text-gray-700 mb-6">
            We've received your message and will respond within one business day.
          </p>
          <button
            onClick={resetForm}
            className="bg-green-700 hover:bg-green-900 text-white py-2 px-6 rounded-lg font-bold transition-colors hover-scale"
          >
            Send Another Message
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="contact-text-container-blur">
      <h3 className="text-xl font-bold text-gray-900 mb-4">Send Us a Message</h3>

      {status === 'error' && (
        <div className="border-2 border-red-500 bg-red-50 rounded-lg p-4 mb-4">
          <p className="text-red-700 text-sm">
            {errorMessage} Please try again or call us at{' '}
            <a href="tel:1-250-492-0296" className="font-bold underline">(250) 492-0296</a>.
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate>
        {/* Honeypot */}
        <div style={{ position: 'absolute', left: '-9999px', opacity: 0 }} aria-hidden="true">
          <label htmlFor="contact-honey">Leave this empty</label>
          <input
            type="text"
            id="contact-honey"
            name="_honey"
            tabIndex={-1}
            autoComplete="off"
            value={formData._honey}
            onChange={handleChange}
          />
        </div>

        {/* Name */}
        <div className="mb-4">
          <label htmlFor="contact-name" className="block text-sm font-semibold text-gray-700 mb-1">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="contact-name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={`contact-form-input w-full rounded-lg border bg-white/80 px-3 py-2 text-gray-900 ${
              errors.name ? 'border-red-500' : 'border-gray-300'
            }`}
            required
          />
          {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
        </div>

        {/* Email */}
        <div className="mb-4">
          <label htmlFor="contact-email" className="block text-sm font-semibold text-gray-700 mb-1">
            Email <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            id="contact-email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={`contact-form-input w-full rounded-lg border bg-white/80 px-3 py-2 text-gray-900 ${
              errors.email ? 'border-red-500' : 'border-gray-300'
            }`}
            required
          />
          {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
        </div>

        {/* Phone + Company row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label htmlFor="contact-phone" className="block text-sm font-semibold text-gray-700 mb-1">
              Phone
            </label>
            <input
              type="tel"
              id="contact-phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="contact-form-input w-full rounded-lg border border-gray-300 bg-white/80 px-3 py-2 text-gray-900"
            />
          </div>
          <div>
            <label htmlFor="contact-company" className="block text-sm font-semibold text-gray-700 mb-1">
              Company
            </label>
            <input
              type="text"
              id="contact-company"
              name="company"
              value={formData.company}
              onChange={handleChange}
              className="contact-form-input w-full rounded-lg border border-gray-300 bg-white/80 px-3 py-2 text-gray-900"
            />
          </div>
        </div>

        {/* Service Type */}
        <div className="mb-4">
          <label htmlFor="contact-service" className="block text-sm font-semibold text-gray-700 mb-1">
            Service Type
          </label>
          <select
            id="contact-service"
            name="serviceType"
            value={formData.serviceType}
            onChange={handleChange}
            className="contact-form-input w-full rounded-lg border border-gray-300 bg-white/80 px-3 py-2 text-gray-900"
          >
            <option value="">Select a service (optional)</option>
            <option value="bare-ground">Bare Ground / Total Vegetation Control</option>
            <option value="turf">Turf & Lawn Weed Control</option>
            <option value="noxious">Noxious Weed Management</option>
            <option value="aquatic">Aquatic Vegetation Control</option>
            <option value="high-rail">High-Rail Herbicide Application</option>
            <option value="site-assessment">Site Assessment</option>
            <option value="other">Other</option>
          </select>
        </div>

        {/* Message */}
        <div className="mb-4">
          <label htmlFor="contact-message" className="block text-sm font-semibold text-gray-700 mb-1">
            Message <span className="text-red-500">*</span>
          </label>
          <textarea
            id="contact-message"
            name="message"
            rows="5"
            value={formData.message}
            onChange={handleChange}
            className={`contact-form-input w-full rounded-lg border bg-white/80 px-3 py-2 text-gray-900 resize-vertical ${
              errors.message ? 'border-red-500' : 'border-gray-300'
            }`}
            required
          />
          {errors.message && <p className="text-red-500 text-xs mt-1">{errors.message}</p>}
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={status === 'submitting'}
          className="w-full bg-green-700 hover:bg-green-900 text-white py-3 px-6 rounded-lg font-bold transition-colors hover-scale disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {status === 'submitting' ? (
            <>
              <span className="contact-spinner" />
              Sending...
            </>
          ) : (
            'Send Message'
          )}
        </button>
      </form>
    </div>
  );
};

export default ContactForm;
